<template>
    <ToolbarAbstract 
        ref="scrollable" 
        class="toolbar-image-layer" 
        @wheel.prevent="handleWheel">

        <div class="content">

            <div v-if="isDebugLayer" class="inner">
                <div class="layer" v-for="key in orderedKeys" :key="key">
                    <div class="label">
                        {{ key }}
                    </div>
                    <div class="frame">
                        <img v-if="thumbnails?.[key]" :src="thumbnails[key]" :alt="key + ' layer'" />
                        <div v-else class="empty">empty</div>
                    </div>
                </div>
            </div>

            <div v-else-if="isCategorySketch" class="inner">
                <div v-for="example in examples" class="layer square clickable">
                    <ViewerImageRaw 
                        :data="example.url" 
                        @click="handleExample(example)" />
                </div>
            </div>

		</div>

    </ToolbarAbstract>
</template>

<script>
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import { mapGetters } from '@generio-ai/library-legacy/plugins/vuex'
import ToolbarAbstract from '@/components/common/toolbar/ToolbarAbstract.vue'
import ViewerImageRaw from '@/components/common/viewer/ViewerImageRaw.vue'

export default {
    name: 'ToolbarImageLayer',
    components: { 
        ToolbarAbstract, 
        ViewerImageRaw 
    },
    props: {
        category: {
            type: String,
            default: null
        },
        thumbnails: {
            type: Object,
            default: () => ({ 
                image: null, 
                marker: null, 
                sketch: null 
            }) 
        }
    },
    computed: {
        ...mapGetters('flows', [
            'isDebugLayer'
        ]),

		isCategorySketch() {
			return this.category === CATEGORY.SKETCH
		},

        orderedKeys() { 
            return [
                'image',
                'marker',
                'sketch'
            ]
        },
        examples() {
			return [
                {
                    url: '/sketches/human-tpose.png',
                    prompt: 'human character in t-pose',
                    augmentation: { styles: [ 'photorealistic' ], poses: [ 't-pose' ], views: [ 'front' ] }
                },
                {
                    url: '/sketches/house.png',
                    prompt: 'house',
                    augmentation: { styles: [ 'photorealistic' ], poses: [], views: [ 'diagonal' ] }
                },
				{
                    url: '/sketches/cactus-in-pot.png',
                    prompt: 'cactus in pot',
                    augmentation: { styles: [ 'photorealistic' ], poses: [], views: [ 'front' ] }
                },
                {
                    url: '/sketches/ice-cream-cone.png',
                    prompt: 'ice cream cone',
                    augmentation: { styles: [ 'photorealistic' ], poses: [], views: [ 'front' ] }
                },
                {
                    url: '/sketches/tree-side.png',
                    prompt: 'tree',
                    augmentation: { styles: [ 'photorealistic' ], poses: [], views: [ 'front' ] }
                },
                {
                    url: '/sketches/diamond-ring.png',
                    prompt: 'diamond ring',
                    augmentation: { styles: [ 'photorealistic' ], poses: [], views: [ 'front' ] }
                },
                {
                    url: '/sketches/car-side-view.png',
                    prompt: 'car',
                    augmentation: { styles: [ 'photorealistic' ], poses: [], views: [ 'left' ] }
                },
                {
                    url: '/sketches/fish-side.png',
                    prompt: 'fish',
                    augmentation: { styles: [ 'photorealistic' ], poses: [], views: [ 'left' ] }
                },
                {
                    url: '/sketches/cat-sitting.png',
                    prompt: 'cat',
                    augmentation: { styles: [ 'photorealistic' ], poses: [], views: [ 'front' ] }
                },
                {
                    url: '/sketches/dog-sitting.png',
                    prompt: 'dog',
                    augmentation: { styles: [ 'photorealistic' ], poses: [], views: [ 'front' ] }
                }
			]
		},
    },
    emits: [
        'example'
    ],
    methods: {
        handleExample(example) {
            this.$emit('example', example)
		},
        handleWheel(event) {
            event.preventDefault()
            const scrollable = this.$refs.scrollable?.$el
            if (!scrollable) return
            if (scrollable.scrollTop === undefined) {
                scrollable.scrollTop = 0
            }
            scrollable.scrollTop += event.deltaY
        }
    }
}
</script>

<style>
.toolbar-image-layer .layer.clickable img {
    transition: border 0.3s ease, transform 0.3s ease;
}

.toolbar-image-layer .layer.clickable:hover {
    border: 2px solid rgba(var(--v-theme-color1));
}

.toolbar-image-layer .layer.clickable:hover img {
    transform: scale(1.2);
}
</style>

<style scoped>
.toolbar-image-layer .content {
	width: 100%;
	height: 100%;
    display: flex;
    flex-direction: column;
    align-self: stretch;
    align-items: center;
    justify-content: flex-start;
    flex: 1 1 auto;
    min-height: 0;
    gap: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
}

.toolbar-image-layer .inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    margin-top: auto;
    margin-bottom: auto;
    width: 100%;
}

.toolbar-image-layer .layer {
	position: relative;
	display: block;
	width: 100%;
	height: auto;
	overflow: hidden;
    flex: 0 0 auto;
    font-size: 0.8rem;
    color: rgba(var(--v-theme-primary));
}

.toolbar-image-layer .layer.square {
    aspect-ratio: 1 / 1;
    border-radius: 8px;
}

.toolbar-image-layer .layer.clickable {
    cursor: pointer;
}

.toolbar-image-layer .layer .label { 
    margin-bottom: 2px; 
    text-transform: uppercase; 
}

.toolbar-image-layer .layer .frame {
    width: 64px;
    height: 64px;
    position: relative;
    background: repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 50% / 8px 8px;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 1px rgba(0,0,0,0.05) inset;
}

.toolbar-image-layer .layer .frame img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    image-rendering: crisp-edges;
    image-rendering: pixelated;
}

.toolbar-image-layer .layer .frame .empty { 
    font-size: 9px; 
    opacity: 0.5; 
}
</style>